import React from 'react';
import { Tag, Tooltip, Progress, Space } from 'antd';
import { 
  CrownOutlined, 
  RocketOutlined, 
  StarOutlined,
  InfoCircleOutlined,
  ClockCircleOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { PLANS } from '../../services/planService';
import usePlan from '../../hooks/usePlan';
import './PlanBadge.css';

/**
 * Composant badge pour afficher le plan utilisateur
 * @param {'tag'|'inline'} [variant='tag'] - `tag` : pastilles Ant Design (rendu
 *   historique) ; `inline` : ligne de texte discrète (icône, nom du plan et,
 *   pour un essai, messages consommés) qui hérite des tokens `--dl-*` du
 *   conteneur — utilisée dans le menu latéral.
 */
const PlanBadge = ({ 
  showProgress = false, 
  showUpgradeLink = true, 
  size = 'default',
  variant = 'tag'
}) => {
  const { 
    currentPlan, 
    planName, 
    isTrialPlan, 
    trialMessagesUsed, 
    trialMaxMessages,
    loading 
  } = usePlan();

  // Configuration des icônes par plan
  const getPlanIcon = (plan) => {
    const icons = {
      [PLANS.DECOUVERTE]: <InfoCircleOutlined />,
      [PLANS.STARTER]: <ThunderboltOutlined />,
      [PLANS.ESSENTIEL]: <RocketOutlined />,
      [PLANS.CROISSANCE]: <StarOutlined />,
      [PLANS.EXCELLENCE]: <CrownOutlined />
    };
    return icons[plan] || <InfoCircleOutlined />;
  };

  // Configuration des couleurs par plan
  const getPlanColor = (plan) => {
    const colors = {
      [PLANS.DECOUVERTE]: 'default',
      [PLANS.STARTER]: 'green',
      [PLANS.ESSENTIEL]: 'blue',
      [PLANS.CROISSANCE]: 'cyan',
      [PLANS.EXCELLENCE]: 'purple'
    };
    return colors[plan] || 'default';
  };

  // Calculer le pourcentage d'utilisation pour les plans d'essai
  const getUsagePercentage = () => {
    if (!isTrialPlan || trialMaxMessages === 0) return 0;
    return Math.round((trialMessagesUsed / trialMaxMessages) * 100);
  };

  // Contenu du tooltip avec détails du plan
  const getTooltipContent = () => {
    if (!currentPlan) return 'Chargement...';

    return (
      <div className="space-y-2">
        <div>
          <strong>{planName}</strong>
          {isTrialPlan && <div className="text-orange-300">Version d'essai</div>}
        </div>
        
        {isTrialPlan && (
          <div>
            <div className="text-sm">
              Messages utilisés: {trialMessagesUsed} / {trialMaxMessages}
            </div>
            {showProgress && (
              <Progress 
                percent={getUsagePercentage()}
                size="small"
                status={getUsagePercentage() >= 90 ? "exception" : "normal"}
                className="mt-1"
              />
            )}
          </div>
        )}
        
        {showUpgradeLink && currentPlan !== PLANS.EXCELLENCE && (
          <div className="text-blue-300 text-sm">
            Cliquez pour voir les options de mise à niveau
          </div>
        )}
      </div>
    );
  };

  // Variante inline : simple texte atténué pendant le chargement
  if (loading && variant === 'inline') {
    return <span className="plan-badge-inline animate-pulse">Chargement…</span>;
  }

  if (loading) {
    return (
      <Tag icon={<InfoCircleOutlined />} className="animate-pulse">
        Chargement...
      </Tag>
    );
  }

  if (!currentPlan) {
    return null;
  }

  const BadgeContent = () => (
    <Space size="small">
      <Tag 
        color={getPlanColor(currentPlan)} 
        icon={getPlanIcon(currentPlan)}
        size={size}
      >
        {planName}
      </Tag>
      
      {isTrialPlan && (
        <Tag 
          color="orange" 
          icon={<ClockCircleOutlined />}
          size={size}
        >
          Essai
        </Tag>
      )}
      
      {showProgress && isTrialPlan && (
        <div className="flex items-center space-x-2">
          <span className="text-xs text-gray-500">
            {trialMessagesUsed}/{trialMaxMessages}
          </span>
          <Progress 
            percent={getUsagePercentage()}
            size="small"
            showInfo={false}
            status={getUsagePercentage() >= 90 ? "exception" : "normal"}
            style={{ width: '60px' }}
          />
        </div>
      )}
    </Space>
  );

  /**
   * Variante inline : une seule ligne « icône Plan · Essai x/y ».
   * Le compteur passe en couleur d'alerte à partir de 90 % d'utilisation.
   */
  const InlineContent = () => (
    <>
      <span className="plan-badge-inline__icon" aria-hidden="true">{getPlanIcon(currentPlan)}</span>
      <span className="plan-badge-inline__name">{planName}</span>
      {isTrialPlan && (
        <span className={getUsagePercentage() >= 90 ? 'plan-badge-inline__alert' : ''}>
          · Essai{showProgress ? ` ${trialMessagesUsed}/${trialMaxMessages}` : ''}
        </span>
      )}
    </>
  );

  if (variant === 'inline') {
    const peutEvoluer = showUpgradeLink && currentPlan !== PLANS.EXCELLENCE;
    return (
      <Tooltip title={getTooltipContent()} placement="bottom">
        {peutEvoluer ? (
          <Link to="/pricing" className="plan-badge-inline plan-badge-inline--link dashboard-focusable">
            <InlineContent />
          </Link>
        ) : (
          <span className="plan-badge-inline"><InlineContent /></span>
        )}
      </Tooltip>
    );
  }

  // Si on peut faire des upgrades et que ce n'est pas le plan Excellence
  if (showUpgradeLink && currentPlan !== PLANS.EXCELLENCE) {
    return (
      <Tooltip title={getTooltipContent()} placement="bottom">
        <Link to="/pricing" className="inline-block">
          <BadgeContent />
        </Link>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={getTooltipContent()} placement="bottom">
      <div className="inline-block">
        <BadgeContent />
      </div>
    </Tooltip>
  );
};

export default PlanBadge; 